import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import { CustomerSourceType } from 'library/types';
import { GridRowId } from '@mui/x-data-grid-premium';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, DialogTitle, DialogContentText } from '@mui/material';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type UpdateCustomerSourceTypeDialogProps = {
  open: boolean;
  customerSourceTypeId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateCustomerSourceTypeDialog = ({
  open,
  customerSourceTypeId,
  handleClose: closeDialog,
  refresh,
}: UpdateCustomerSourceTypeDialogProps) => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!title) {
      setError(true);
      return;
    }

    const postData: CustomerSourceType = {
      id: customerSourceTypeId as number,
      title,
    };

    try {
      setLoading(true);

      const response = await apiService.updateCustomerSourceType(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="customerSourceTypes.updateCustomerSourceTypeCompleted"
            defaultMessage="Müşteri kaynağı düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getCustomerSourceTypeInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificCustomerSourceType(customerSourceTypeId as number);

      if (response.isSuccess) {
        setTitle(response.data.title);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [customerSourceTypeId]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setTitle('');
    setError(false);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(event.target.value);

  // useEffect hooks
  useEffect(() => {
    if (title) {
      setError(false);
    }
  }, [title]);

  useEffect(() => {
    if (open && customerSourceTypeId) {
      getCustomerSourceTypeInfos();
    }
  }, [open, customerSourceTypeId, getCustomerSourceTypeInfos]);

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="customerSourceTypes.updateCustomerSourceType" defaultMessage="Müşteri Kaynağı Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="customerSourceTypes.updateCustomerSourceTypeText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili müşteri kaynağı için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <TextField
            label={<FormattedMessage id="customerSourceTypes.title" defaultMessage="Adı" />}
            fullWidth
            size="small"
            onChange={onChangeHandler}
            value={title}
            error={error}
            helperText={
              error && <FormattedMessage id="validations.required" defaultMessage="Bu alan boş bırakılamaz" />
            }
          />
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateCustomerSourceTypeDialog;
